import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetJson, DeleteJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';

const initialState = {
  soilTypes: [],
  soilType: {},
  soilTypesLoading: STATE_STATUS.IDLE,
  soilTypesError: null,
};

export const selectAllSoilTypes = (state) => state.soilTypes;
export const selectSoilTypes = (state) => state.soilTypes.soilTypes;
export const selectSoilTypeId = (state) => state.soilTypes.soilType.id;
export const getSoilTypesLoading = (state) => state.soilTypes.soilTypesLoading;

export const fetchSoilTypes = createAsyncThunk(
  'soilTypes/fetchSoilTypes',
  async () => {
    const url = `${REST_API.SOIL_TYPES}`;
    return GetJson(url).then((response) => response.json());
  },
  {
    condition: (args, { getState }) => {
      const { soilTypes: state } = getState();
      return state.soilTypesLoading !== STATE_STATUS.LOADING;
    },
  },
);

export const deleteSoilType = createAsyncThunk('soilTypes/deleteSoilType', async (id, { rejectWithValue }) => {
  const url = `${REST_API.SOIL_TYPES}${id}`;
  const response = await DeleteJson(url);
  const result = await response.json();
  if (response.status === 200) {
    return result;
  } else {
    return rejectWithValue({
      status: response.status,
      message: result || 'Update failed',
    });
  }
});

export const addSoilType = createAsyncThunk('soilTypes/addSoilType', async (soilType, { rejectWithValue }) => {
  const url = `${REST_API.SOIL_TYPES}`;
  const response = await PostJson(url, soilType);
  const result = await response.json();

  if (response.status === 200) {
    return result;
  } else {
    return rejectWithValue({
      status: response.status,
      message: result || 'Update failed',
    });
  }
});

export const updateSoilType = createAsyncThunk('soilTypes/updateSoilType', async (soilType, { rejectWithValue }) => {
  const url = `${REST_API.SOIL_TYPES}${soilType.id}`;
  const response = await PutJson(url, soilType);
  const result = await response.json();

  if (response.status === 200) {
    const newResult = { ...result };
    newResult.ConcurrencyControlNumber += 1;
    return newResult;
  } else {
    return rejectWithValue({
      status: response.status,
      message: result || 'Update failed',
    });
  }
});

export const soilTypesSlice = createSlice({
  name: 'soilTypes',
  initialState,
  reducers: {
    setSoilType(state, action) {
      state.soilType = action.payload;
    },
  },
  extraReducers: {
    [fetchSoilTypes.pending]: (state) => {
      state.soilTypesLoading = STATE_STATUS.LOADING;
    },
    [fetchSoilTypes.fulfilled]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.SUCCESS;
      state.soilTypes = action.payload;
      if (isValueEmpty(state.soilType) || state.soilType.id < 1) {
        state.soilType = action.payload.length > 0 ? action.payload[0] : {};
      }
    },
    [fetchSoilTypes.rejected]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.FAILED;
      state.soilTypesError = action.error.message;
    },
    [deleteSoilType.pending]: (state) => {
      state.soilTypesLoading = STATE_STATUS.LOADING;
    },
    [deleteSoilType.fulfilled]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.SUCCESS;
      state.soilTypes = state.soilTypes.filter((st) => st.Id !== action.payload);
      if (state.soilType.id === action.payload) state.soilType = state.soilTypes[0] || {};
    },
    [deleteSoilType.rejected]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.FAILED;
      state.soilTypesError = action.error.message;
    },
    [addSoilType.pending]: (state) => {
      state.soilTypesLoading = STATE_STATUS.LOADING;
    },
    [addSoilType.fulfilled]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.SUCCESS;
      if (action.payload) {
        state.soilTypes.push(action.payload);
        state.soilType = action.payload; // Set the newly added soil type as current
      }
    },
    [addSoilType.rejected]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.FAILED;
      state.soilTypesError = action.error.message;
    },
    [updateSoilType.pending]: (state) => {
      state.soilTypesLoading = STATE_STATUS.LOADING;
    },
    [updateSoilType.fulfilled]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.SUCCESS;
      if (action.payload) {
        const index = state.soilTypes.findIndex((st) => st.Id === action.payload.Id);
        if (index !== -1) {
          state.soilTypes[index] = action.payload;
          state.soilType = action.payload; // Update the current soil type
        }
      }
    },
    [updateSoilType.rejected]: (state, action) => {
      state.soilTypesLoading = STATE_STATUS.FAILED;
      state.soilTypesError = action.error.message;
    },
  },
});

// Utility function for soil type processing (similar to getjsonObject)
export const processSoilTypeData = (soilType, soilTypesList) => {
  if (!soilType || isValueEmpty(soilTypesList)) return soilType;
  const existingSoilType = soilTypesList.find((st) => st.id === soilType.id) || {};
  return { ...existingSoilType, ...soilType }; // Merge existing with updates
};

export const { setSoilType } = soilTypesSlice.actions;
